import { useState } from "react";
import {
  Box, Paper, TextField, InputAdornment, Alert, Button, Chip,
} from "@mui/material";
import { PageHeader } from "@/components/PageHeader";
import { RowActions } from "@/components/RowActions";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { usersApi, type UserListDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { UserForm } from "./UserForm";
import { ResetPasswordDialog } from "./ResetPasswordDialog";
import { formatDate } from "@/shared/datetime";

export function UsersPage() {
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [resetPwdId, setResetPwdId] = useState<string | null>(null);

  const canCreate = useAuth(s => s.hasPermission("admin.user.create"));
  const canUpdate = useAuth(s => s.hasPermission("admin.user.edit"));
  const canReset = useAuth(s => s.hasPermission("admin.user.reset_password"));
  const canUnlock = useAuth(s => s.hasPermission("admin.user.unlock"));
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["users", search, pagination.page, pagination.pageSize],
    queryFn: () => usersApi.list({
      search: search || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const unlockMutation = useMutation({
    mutationFn: (id: string) => usersApi.unlock(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });

  const openCreate = () => { setEditingId(null); setFormOpen(true); };
  const openEdit = (id: string) => { setEditingId(id); setFormOpen(true); };

  const columns: GridColDef<UserListDto>[] = [
    { field: "email", headerName: "Email", flex: 1, minWidth: 220 },
    { field: "fullName", headerName: "Full name", flex: 1, minWidth: 180 },
    {
      field: "isActive", headerName: "Status", width: 100,
      renderCell: (p) => (
        <Chip size="small" label={p.value ? "Active" : "Inactive"} color={p.value ? "success" : "default"} />
      ),
    },
    {
      field: "isLockedOut", headerName: "Locked", width: 100,
      renderCell: (p) => p.value
        ? <Chip size="small" label="Locked" color="error" />
        : <Chip size="small" label="OK" variant="outlined" />,
    },
    {
      field: "mfaEnabled", headerName: "MFA", width: 80,
      renderCell: (p) => p.value ? <Chip size="small" label="On" color="info" /> : null,
    },
    { field: "branchAccessCount", headerName: "Branches", width: 100, type: "number" },
    {
      field: "createdAt", headerName: "Created", width: 130,
      valueFormatter: (v) => v ? formatDate(v as string) : "",
    },
    {
      field: "actions", headerName: "", width: 130, sortable: false, filterable: false,
      renderCell: (p) => (
        <RowActions actions={[
          { label: "Edit", icon: <EditIcon fontSize="small" />, hidden: !canUpdate, onClick: () => openEdit(p.row.id) },
          { label: "Reset password", icon: <VpnKeyIcon fontSize="small" />, hidden: !canReset, onClick: () => setResetPwdId(p.row.id) },
          // Unlock only means something for a locked account; it stays out of the row otherwise.
          {
            label: "Unlock", icon: <LockOpenIcon fontSize="small" />,
            hidden: !canUnlock || !p.row.isLockedOut,
            onClick: () => unlockMutation.mutate(p.row.id),
          },
        ]} />
      ),
    },
  ];

  return (
    <Box>
      <PageHeader breadcrumbs={["Administration"]} title="Users"
        actions={<>{canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New User
          </Button>
        )}</>}
      />
      <Paper sx={{ p: 2 }}>
        <TextField
          placeholder="Search by email or name"
          fullWidth size="small" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 2 }}
        />
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {unlockMutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(unlockMutation.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowDoubleClick={(p) => canUpdate && openEdit(p.row.id)}
          />
        </Box>
      </Paper>

      <UserForm open={formOpen} onClose={() => setFormOpen(false)} userId={editingId} />
      <ResetPasswordDialog userId={resetPwdId} open={!!resetPwdId} onClose={() => setResetPwdId(null)} />
    </Box>
  );
}
